import { getErrorMessage } from 'shared/api/helpers/error-helper';
import { IUpdateBusinessDto } from 'shared/api/business/interfaces/update-business-info-dto.interface';
import { instance } from 'shared/api/instance';
import { config } from 'shared/config';
import { ICardAdministratorInfo } from 'shared/ui/administrator-mini-card/administrator-mini-card.interface';
import { ICardManagerInfo } from 'shared/ui/manager-mini-card/manager-mini-card.interface';
import { BusinessErrors, BusinessErrorsEnum } from '../consts/business.error';
import { IBusinessInfoDto } from '../interfaces/business-info-dto.interface';
import { ICreateAdministratorDto } from '../interfaces/create-administrator-dto.interface';
import { ICreateBusinessDto } from '../interfaces/create-business-dto.interface';
import { ICreateEmployeeDto } from '../interfaces/create-employee-dto.interface';
import { ICreateManagerDto } from '../interfaces/create-manager-dto.interface';
import { ICreateOwnerDto } from '../interfaces/create-owner-dto.interface';
import { ICreatePointDto } from '../interfaces/create-point-dto.interface';
import { IEmployeeInfo } from '../interfaces/employee-info.interface';
import { IGetAdministratorsDto } from '../interfaces/get-administrators-dto.interface';
import { IGetBusinessHeaderInfoDto } from '../interfaces/get-business-header-info-dto.interface';
import { IGetBusinessesDto } from '../interfaces/get-businesses-dto.interface';
import { IGetEmployeeInfoParams } from '../interfaces/get-employee-Info.params.interface';
import { IGetEmployeesDto } from '../interfaces/get-employees-dto.interface';
import { IGetManagersForBusinessDto } from '../interfaces/get-managers-for-business-dto.interface';
import { IGetManagersForPointDto } from '../interfaces/get-managers-for-point-dto.interface';
import { IGetPointHeaderInfoDto } from '../interfaces/get-point-header-info-dto.interface';
import { IGetPointListDto } from '../interfaces/get-point-list-dto.interface';
import { IGetPointsDto } from '../interfaces/get-points-dto.interface';
import { IPointListDto } from '../interfaces/point-list-dto.interface';
import { IPointMiniCardDto } from '../interfaces/point-mini-card-dto.interface';
import { IUpdateOwnerInfoDto } from '../interfaces/update-owner-info-dto.interface';
import { IUpdatePointDto } from '../interfaces/update-point-info-dto.interface';
import { IBusinessHeaderData } from 'entities/business/interfaces/business-card.interface';
import { IPointHeaderData } from 'entities/business/interfaces/point-card.interface';
import { IAccountGetInfoParams } from '../interfaces/account-get-info.params.interface';
import { IAccountInfoProps } from '../interfaces/account-info-props.interface';
import { IUpdateManagerInfoDto } from '../interfaces/update-manager-info.interface';
import { IUpdateEmployeeInfoDto } from '../interfaces/update-employee-info.interface';
import { IUpdateAdministratorInfoDto } from '../interfaces/update-administrator-info.interface';
import { IAdministratorInfoProps } from '../interfaces/administrator-info.props.interface';
import { IManagerInfoProps } from '../interfaces/manager-info.props.interface';
import { IEmployeeInfoProps } from '../interfaces/employee-info.props.interface';
import { IGetManagersAccountListProps } from '../interfaces/get-managers-account-list.props.interface';
import { IManagersAccountList } from '../interfaces/managers-account-list.interface';
import { IAccountSetEmployeeProps } from '../interfaces/account-set-employee.props.interface';
import { IDeleteEmployeeProps } from '../interfaces/delete-employee.props.interface';
import { IDeleteManagerProps } from '../interfaces/delete-manager.props.interface';
import { IDeleteAdministratorProps } from '../interfaces/delete-administrator.props.interface';
import { IDeletePointProps } from '../interfaces/delete-point.props.interface';
import { IGetLicenseInfoProps } from '../interfaces/get-license-info.props.interface';
import { ILicensePaymentProps } from '../interfaces/license-payment.props.interface';
import { IPaymentHistoryItem } from '../interfaces/payment-history-item.interface';
import { IRefundCalculation } from '../interfaces/refund-calculation.interface';

const API = instance(config.API_BUSINESS);

export class BusinessApi {
  public static async createOwner(dto: ICreateOwnerDto): Promise<void> {
    const url = '/owner';
    try {
      return await API.post(url, dto);
    } catch (error) {
      const err = error as { response?: { status: number } };
      switch (err.response?.status) {
        case 409:
          throw new Error(BusinessErrors.CREATE_OWNER[409]);
        case 400:
          throw new Error(BusinessErrors.CREATE_OWNER[400]);
        default:
          throw new Error(BusinessErrorsEnum.SIGN_UP_OWNER);
      }
    }
  }
  public static async licensePayment(
    dto: ILicensePaymentProps
  ): Promise<string> {
    const url = '/owner/license/payment';
    try {
      return (await API.post(url, dto)).data;
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BusinessErrorsEnum.LICENSE_PAYMENT)
      );
    }
  }

  public static async getPayments(): Promise<IPaymentHistoryItem[]> {
    const url = '/owner/license/payments';
    try {
      return (await API.get(url)).data;
    } catch (error) {
      throw new Error(getErrorMessage(error, BusinessErrorsEnum.GET_PAYMENTS));
    }
  }

  public static async refundCalculate(
    paymentId: string
  ): Promise<IRefundCalculation> {
    const url = '/owner/license/refund/calculate';
    try {
      return (await API.post(url, { paymentId })).data;
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BusinessErrorsEnum.REFUND_CALCULATE)
      );
    }
  }

  public static async refundPayment(
    paymentId: string
  ): Promise<IRefundCalculation> {
    const url = '/owner/license/refund';
    try {
      return (await API.post(url, { paymentId })).data;
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BusinessErrorsEnum.REFUND_PAYMENT)
      );
    }
  }

  public static async getAccountInfo(): Promise<IGetBusinessesDto> {
    try {
      const url = '/account/get-info';
      return (await API.get(url)).data;
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BusinessErrorsEnum.GET_ACCOUNT_INFO)
      );
    }
  }
  public static async getOwnerLicenseInfo(): Promise<IGetLicenseInfoProps> {
    try {
      const url = '/owner/license';
      return (await API.get(url)).data;
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BusinessErrorsEnum.GET_OWNER_LICENSE_INFO)
      );
    }
  }

  public static async getBusinessesInfo(
    dto: IGetBusinessesDto
  ): Promise<IBusinessInfoDto[]> {
    try {
      const url = '/business/by-owner-id/' + dto.id;
      return (await API.get(url)).data;
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BusinessErrorsEnum.GET_BUSINESSES_INFO)
      );
    }
  }
  public static async getPointsInfo(
    dto: IGetPointsDto
  ): Promise<IPointMiniCardDto[]> {
    try {
      if (dto.businessId) {
        const url = '/point/business-card-info/' + dto.businessId;
        return (await API.get(url)).data;
      }
      throw new Error(BusinessErrorsEnum.GET_POINTS_INFO);
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BusinessErrorsEnum.GET_POINTS_INFO)
      );
    }
  }
  public static async getPointList(
    dto: IGetPointListDto
  ): Promise<IPointListDto[]> {
    try {
      if (dto.businessId) {
        const url = '/point/list';
        return (await API.get(url, { params: dto })).data;
      }
      throw new Error(BusinessErrorsEnum.GET_POINTS_INFO);
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BusinessErrorsEnum.GET_POINTS_INFO)
      );
    }
  }

  public static async getAdministratorsInfo(
    dto: IGetAdministratorsDto
  ): Promise<ICardAdministratorInfo[]> {
    try {
      const url = '/administrator/business-card-info/' + dto.businessId;
      return (await API.get(url)).data;
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BusinessErrorsEnum.GET_ADMINISTRATORS_INFO)
      );
    }
  }
  public static async getEmployeesInfo(
    dto: IGetEmployeesDto
  ): Promise<IEmployeeInfo[]> {
    try {
      const url = '/employee/point-card-info/' + dto.pointId;
      return (await API.get(url)).data;
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BusinessErrorsEnum.GET_EMPLOYEES_INFO)
      );
    }
  }

  public static async getManagersInfoForBusiness(
    dto: IGetManagersForBusinessDto
  ): Promise<ICardManagerInfo[]> {
    try {
      const url = '/manager/business-card-info/' + dto.businessId;
      return (await API.get(url)).data;
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BusinessErrorsEnum.GET_MANAGERS_INFO)
      );
    }
  }
  public static async getManagersInfoForPoint(
    dto: IGetManagersForPointDto
  ): Promise<ICardManagerInfo[]> {
    try {
      const url = '/manager/point-card-info/' + dto.pointId;
      return (await API.get(url)).data;
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BusinessErrorsEnum.GET_MANAGERS_INFO)
      );
    }
  }

  public static async createBusiness(dto: ICreateBusinessDto): Promise<void> {
    try {
      const url = '/business';
      return await API.post(url, dto);
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BusinessErrorsEnum.SIGN_UP_BUSINESS)
      );
    }
  }

  public static async updateBusinessInfo(
    dto: IUpdateBusinessDto
  ): Promise<IBusinessHeaderData> {
    try {
      const { id, ...data } = dto;
      const url = '/business/' + id;
      return (await API.patch(url, data)).data;
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BusinessErrorsEnum.UPDATE_BUSINESS_INFO)
      );
    }
  }

  public static async updatePointInfo(
    dto: IUpdatePointDto
  ): Promise<IPointHeaderData> {
    try {
      const { id, ...data } = dto;
      const url = '/point/' + id;
      return (await API.patch(url, data)).data;
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BusinessErrorsEnum.UPDATE_POINT_INFO)
      );
    }
  }

  public static async createPoint(dto: ICreatePointDto): Promise<void> {
    try {
      const url = '/point';
      return await API.post(url, dto);
    } catch (error) {
      throw new Error(getErrorMessage(error, BusinessErrorsEnum.SIGN_UP_POINT));
    }
  }

  public static async createAdministrator(
    dto: ICreateAdministratorDto
  ): Promise<void> {
    try {
      const url = '/administrator';
      return await API.post(url, dto);
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BusinessErrorsEnum.SIGN_UP_ADMINISTRATOR)
      );
    }
  }

  public static async createManager(dto: ICreateManagerDto): Promise<void> {
    try {
      const url = '/manager';
      return await API.post(url, dto);
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BusinessErrorsEnum.SIGN_UP_MANAGER)
      );
    }
  }

  public static async createEmployee(dto: ICreateEmployeeDto): Promise<void> {
    try {
      const url = '/employee';
      return await API.post(url, dto);
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BusinessErrorsEnum.SIGN_UP_EMPLOYEE)
      );
    }
  }
  public static async deleteEmployee(dto: IDeleteEmployeeProps): Promise<void> {
    try {
      const url = '/employee/' + dto.employeeId;
      return await API.delete(url);
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BusinessErrorsEnum.DELETE_EMPLOYEE)
      );
    }
  }
  public static async deleteManager(dto: IDeleteManagerProps): Promise<void> {
    try {
      const url = '/manager/' + dto.managerId;
      return await API.delete(url);
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BusinessErrorsEnum.DELETE_MANAGER)
      );
    }
  }
  public static async deleteAdministrator(
    dto: IDeleteAdministratorProps
  ): Promise<void> {
    try {
      const url = '/administrator/' + dto.administratorId;
      return await API.delete(url);
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BusinessErrorsEnum.DELETE_ADMINISTRATOR)
      );
    }
  }
  public static async deletePoint(dto: IDeletePointProps): Promise<void> {
    try {
      const url = '/point/' + dto.pointId;
      return await API.delete(url);
    } catch (error) {
      throw new Error(getErrorMessage(error, BusinessErrorsEnum.DELETE_POINT));
    }
  }
  public static async accountSetEmployee(
    dto: IAccountSetEmployeeProps
  ): Promise<void> {
    try {
      const url = '/account/set-employee';
      return await API.patch(url, dto);
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BusinessErrorsEnum.SIGN_UP_EMPLOYEE)
      );
    }
  }
  public static async getEmployeeInfo(
    dto: IGetEmployeeInfoParams
  ): Promise<IEmployeeInfo> {
    try {
      const url = '/employee/card-info/' + dto.employeeId;
      return (await API.get(url, { params: dto })).data;
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BusinessErrorsEnum.SIGN_UP_EMPLOYEE)
      );
    }
  }
  public static async accountGetInfo(
    dto: IAccountGetInfoParams
  ): Promise<IAccountInfoProps> {
    try {
      const url = '/account/get-user/' + dto.accountId;
      return (await API.get(url, { params: dto })).data;
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BusinessErrorsEnum.ACCOUNT_GET_INFO)
      );
    }
  }
  public static async updateManagerInfo(
    dto: IUpdateManagerInfoDto
  ): Promise<IManagerInfoProps> {
    try {
      const { managerId, ...data } = dto;

      const url = '/manager/' + managerId;
      return (await API.patch(url, data)).data;
    } catch (error) {
      const err = error as { response?: { status: number } };
      switch (err.response?.status) {
        case 400:
          throw new Error(BusinessErrors.UPDATE_DATA[400]);
        case 403:
          throw new Error(BusinessErrors.UPDATE_DATA[403]);
        case 404:
          throw new Error(BusinessErrors.UPDATE_DATA[404]);
        default:
          throw new Error(BusinessErrorsEnum.UPDATE_PROFILE_INFO);
      }
    }
  }

  public static async updateAdministratorInfo(
    dto: IUpdateAdministratorInfoDto
  ): Promise<IAdministratorInfoProps> {
    try {
      const { adminId, ...data } = dto;

      const url = '/administrator/' + adminId;
      return (await API.patch(url, data)).data;
    } catch (error) {
      const err = error as { response?: { status: number } };
      switch (err.response?.status) {
        case 400:
          throw new Error(BusinessErrors.UPDATE_DATA[400]);
        case 403:
          throw new Error(BusinessErrors.UPDATE_DATA[403]);
        case 404:
          throw new Error(BusinessErrors.UPDATE_DATA[404]);
        default:
          throw new Error(BusinessErrorsEnum.UPDATE_PROFILE_INFO);
      }
    }
  }
  public static async updateEmployeeInfo(
    dto: IUpdateEmployeeInfoDto
  ): Promise<IEmployeeInfoProps> {
    try {
      const { employeeId, ...data } = dto;

      const url = '/employee/' + employeeId;
      return (await API.patch(url, data)).data;
    } catch (error) {
      const err = error as { response?: { status: number } };
      switch (err.response?.status) {
        case 400:
          throw new Error(BusinessErrors.UPDATE_DATA[400]);
        case 403:
          throw new Error(BusinessErrors.UPDATE_DATA[403]);
        case 404:
          throw new Error(BusinessErrors.UPDATE_DATA[404]);
        default:
          throw new Error(BusinessErrorsEnum.UPDATE_PROFILE_INFO);
      }
    }
  }

  public static async updateOwnerInfo(dto: IUpdateOwnerInfoDto): Promise<void> {
    try {
      const { ownerId, ...data } = dto;
      const url = '/owner/' + ownerId;
      return await API.patch(url, data);
    } catch (error) {
      const err = error as { response?: { status: number } };
      switch (err.response?.status) {
        case 400:
          throw new Error(BusinessErrors.UPDATE_DATA[400]);
        case 403:
          throw new Error(BusinessErrors.UPDATE_DATA[403]);
        case 404:
          throw new Error(BusinessErrors.UPDATE_DATA[404]);
        default:
          throw new Error(BusinessErrorsEnum.UPDATE_OWNER_PROFILE_INFO);
      }
    }
  }
  public static async getBusinessHeaderInfo(
    dto: IGetBusinessHeaderInfoDto
  ): Promise<IBusinessHeaderData> {
    try {
      const { businessId, ...data } = dto;
      const url = '/business/card-info/' + businessId;
      return (await API.get(url, { params: data })).data;
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BusinessErrorsEnum.GET_BUSINESS_CARD_INFO)
      );
    }
  }
  public static async getPointHeaderInfo(
    dto: IGetPointHeaderInfoDto
  ): Promise<IPointHeaderData> {
    try {
      const { pointId, ...data } = dto;
      const url = '/point/card-info/' + pointId;
      return (await API.get(url, { params: data })).data;
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BusinessErrorsEnum.GET_POINT_CARD_INFO)
      );
    }
  }
  public static async getManagersAccountList(
    dto: IGetManagersAccountListProps
  ): Promise<IManagersAccountList[]> {
    try {
      const { pointId, ...data } = dto;
      const url = '/account/list/' + pointId;
      return (await API.get(url, { params: data })).data;
    } catch (error) {
      throw new Error(
        getErrorMessage(error, BusinessErrorsEnum.GET_MANAGERS_ACCOUNT_LIST)
      );
    }
  }
}
